import React, { useState, useMemo } from "react";

import { 
  useReactTable, 
  getCoreRowModel, 
  getSortedRowModel, 
  flexRender 
} from '@tanstack/react-table';

import { 
  AdvancedFilterPanel, 
  PdfModal, 
  ProductDetailPanel, 
  PdfExportWarningModal 
} from "./components";

import { AssortmentSelect } from "../shared";

import { i18n } from "../../../../translate/i18n";
import { textFormatter } from "../../../../utils/formatter/formatter";

const { t } = i18n;

const SortArrow = ({ col, sort }) => {
  const isActive = sort.col === col;
  const isAsc = isActive && sort.dir === 'asc';
  return (
    <span style={{ marginLeft: 4, display: 'inline-flex', flexDirection: 'column', gap: 4, verticalAlign: 'middle' }}>
      <svg width="8" height="5" viewBox="0 0 10 6" fill="none" stroke={isActive && isAsc ? '#E8773A' : '#ccc'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="1 5 5 1 9 5" />
      </svg>
      <svg width="8" height="5" viewBox="0 0 10 6" fill="none" stroke={isActive && !isAsc ? '#E8773A' : '#ccc'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="1 1 5 5 9 1" />
      </svg>
    </span>
  );
};

export const ProductPerformanceTab = ({ actions, ctx, uiState }) => {

  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  const [expandedId, setExpandedId] = useState(null);
  const [sorting, setSorting] = useState([{ id: 'score', desc: true }]);

  const [pdfExportSummary, setPdfExportSummary] = useState(null);

  const currentSort = sorting[0];
  const ppSort = { col: currentSort?.id, dir: currentSort?.desc ? 'desc' : 'asc' };

  const { 
    setPpCodeFilter, 
    setProductReportSeason, 
    setPpAdvancedFilters, 
    clearPpAdvancedFilters, 
    setPpExpandedProductId,
    setPdfExportProductIds,
    setPdfExportSeasonId
  } = actions;

  const { 
    ppFilterOptions, 
    ppColumns, 
    ppProducts, 
    productTagData, 
    ppSeasonOptions,
    ppExpandedProductId,
    pdfExportImages,
    ppAllProducts
  } = ctx;

  const { 
    ppCodeFilter, 
    productReportSeason, 
    isLoadingProductTag, 
    ppAdvancedFilters,
    isLoadingPdfExportImages
  } = uiState;

  const handleExpand = (id, productId) => {
    const isExpanding = expandedId !== id;
    setExpandedId(isExpanding ? id : null);
    setPpExpandedProductId(isExpanding ? productId : null);
  };

  const totalCount = ppProducts?.length ?? 0;

  // total da coleção (sem filtros) para o ranking não perder a referência
  const seasonTotalCount = useMemo(() =>
    ppAllProducts?.filter(item => item?.season?.seasonId === productReportSeason)?.length ?? 0,
  [ppAllProducts, productReportSeason]);

  const advancedCount = useMemo(() =>
    Object.values(ppAdvancedFilters).reduce((acc, value) => acc + (Array.isArray(value) ? value.length : value ? 1 : 0), 0),
  [ppAdvancedFilters]);

  const table = useReactTable({
    data: ppProducts ?? [],
    columns: ppColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <section style={{
        backgroundColor: '#fff', border: '1px solid #e5e5e5',
        borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        padding: '16px 20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <hgroup>
            <p style={{ fontSize: '1rem', fontWeight: '700', color: '#1a1a1a' }}>{textFormatter(t("performanceByProduct"))}</p>
            <p style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 2 }}>Análise detalhada dos produtos vendidos na coleção</p>
          </hgroup>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className={`pp-search${ppCodeFilter ? ' pp-search--active' : ''}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>

              <input
                type="text"
                placeholder="Filtro rápido (código)"
                value={ppCodeFilter}
                onChange={e => setPpCodeFilter(e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '13px', color: '#333', width: '100%', backgroundColor: 'transparent' }}
              />
              {ppCodeFilter && (
                <button onClick={() => setPpCodeFilter('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '16px', padding: 0, lineHeight: 1 }}>×</button>
              )}
            </div>

            <AssortmentSelect 
              label="" 
              value={productReportSeason} 
              onChange={setProductReportSeason} 
              options={ppSeasonOptions} />

            <button
              onClick={() => setShowFilterPanel(true)}
              className={`pp-filter-btn${advancedCount > 0 ? ' pp-filter-btn--active' : ''}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E8773A" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6"/>
                <line x1="4" y1="12" x2="20" y2="12"/>
                <line x1="4" y1="18" x2="20" y2="18"/>
                <circle cx="9" cy="6" r="2" fill="#E8773A" stroke="none"/>
                <circle cx="15" cy="12" r="2" fill="#E8773A" stroke="none"/>
                <circle cx="9" cy="18" r="2" fill="#E8773A" stroke="none"/>
              </svg>
              Filtros Avançados
              {advancedCount > 0 && (
                <span style={{
                  backgroundColor: '#E8773A', color: '#fff', fontSize: '10px', fontWeight: '700',
                  width: 16, height: 16, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {advancedCount}
                </span>
              )}
            </button>

            <button onClick={() => setShowPdfModal(true)} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: '8px',
              border: 'none', backgroundColor: '#E8773A', color: '#fff',
              fontSize: '13px', fontWeight: '700', cursor: 'pointer',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              Gerar PDF
            </button>
          </div>
        </div>
      </section>

      <section style={{
        backgroundColor: '#fff', border: '1px solid #e5e5e5',
        borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        overflow: 'hidden', flex: 1, overflowY: 'auto',
        maxHeight: '80%', minHeight: 0,
      }}>
        <table className="table-hover" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 40 }}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} style={{ borderBottom: '1.5px solid #e8e8e8', backgroundColor: 'rgba(230,230,230,1)' }}>
                {headerGroup.headers.map(header => {
                  const colId = header.column.id;
                  const isExpand = colId === 'expand';
                  const isIcon = colId === 'icon';
                  const isProduto = colId === 'produto';
                  return (
                    <th
                      key={header.id}
                      onClick={header.column.columnDef.enableSorting ? () => header.column.toggleSorting() : undefined}
                      style={{
                        width: isExpand ? 44 : isIcon ? 40 : undefined,
                        padding: isExpand ? '10px 0 10px 12px' : '10px 12px',
                        textAlign: isProduto ? 'left' : 'center',
                        fontSize: '11px', fontWeight: '700', color: '#555', letterSpacing: '0.05em',
                        cursor: header.column.columnDef.enableSorting ? 'pointer' : 'default',
                        userSelect: 'none', whiteSpace: 'nowrap',
                      }}
                    >
                      {!isExpand && !isIcon && (
                        <>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.columnDef.enableSorting && <SortArrow col={colId} sort={ppSort} />}
                        </>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {totalCount === 0 ? (
              <tr>
                <td colSpan={ppColumns.length} style={{ padding: '40px', textAlign: 'center', color: '#aaa', fontSize: '13px' }}>
                  Nenhum produto encontrado com os filtros aplicados.
                </td>
              </tr>
            ) : table.getRowModel().rows.map((row, index) => {
              const item = row.original;
              const id = item.productId;
              const isExpanded = expandedId === id;
              const isStriped = index % 2 === 1;

              return (
                <React.Fragment key={`${id}-${item?.name}`}>
                  <tr
                    className={`pp-row${isExpanded ? ' pp-row--expanded' : ''}${isStriped ? ' pp-row--striped' : ''}`}>

                    {row.getVisibleCells().map(cell => {
                      const colId = cell.column.id;
                      const isExpand = colId === 'expand';
                      const isIcon = colId === 'icon';
                      const isProduto = colId === 'produto';

                      if (isExpand) return (
                        <td key={cell.id} style={{ padding: '10px 0 10px 12px', width: 32 }}>
                          <button
                            onClick={() => handleExpand(id, item.productId)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: isExpanded ? '#E8773A' : '#bbb',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              padding: '2px 4px', transition: 'color 0.15s',
                            }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              {isExpanded
                                ? <polyline points="6 9 12 15 18 9"/>
                                : <polyline points="9 6 15 12 9 18"/>
                              }
                            </svg>
                          </button>
                        </td>
                      );

                      if (isIcon) return (
                        <td key={cell.id} style={{ padding: '10px 4px', width: 40 }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: '8px',
                            backgroundColor: '#fff6f0', border: '1px solid #fde8d5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#E8773A" strokeWidth="2">
                              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                              <line x1="7" y1="7" x2="7.01" y2="7"/>
                            </svg>
                          </div>
                        </td>
                      );

                      return (
                        <td
                          key={cell.id}
                          style={{ padding: '10px 12px', textAlign: isProduto ? 'left' : 'center' }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>

                  {isExpanded && (
                    <tr key={`${id}-detail`}>
                      <td colSpan={ppColumns.length} style={{ padding: 0 }}>
                        <ProductDetailPanel
                          ctx={{
                            product: item,
                            total: seasonTotalCount,
                          }}
                          uiState={{
                            productTagData: ppExpandedProductId === item.productId ? productTagData : null,
                            isLoadingProductTag: ppExpandedProductId === item.productId ? isLoadingProductTag : false
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </section>

      {showFilterPanel && (
        <AdvancedFilterPanel
          actions={{ 
            setFilters: setPpAdvancedFilters, 
            clearFilters: clearPpAdvancedFilters,
            onClose: () => setShowFilterPanel(false)
          }}
          ctx={{ filterOptions: ppFilterOptions, totalCount }}
          uiState={{ filters: ppAdvancedFilters }}
        />
      )}

      {showPdfModal && (
        <PdfModal
          actions={{ 
            onClose: () => setShowPdfModal(false), 
            setPdfExportProductIds, 
            setPdfExportSeasonId,
            onExportComplete: setPdfExportSummary,
          }}
          ctx={{ 
            seasonOptions: ppSeasonOptions, 
            filterOptions: ppFilterOptions, 
            productReportSeason: productReportSeason,
            activeFilters: ppAdvancedFilters,
            pdfExportImages: pdfExportImages,
            products: ppAllProducts, 
          }}
          uiState={{ isLoadingPdfExportImages: isLoadingPdfExportImages }}
        />
      )}

      {pdfExportSummary && (
        <PdfExportWarningModal
          actions={{ onClose: () => setPdfExportSummary(null) }}
          ctx={{ missingCount: pdfExportSummary.missingCount, total: pdfExportSummary.total }}
          uiState={{ isOpen: true }}
        />
      )}
    </div>
  );
};